/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  Column,
  Row,
  FlexGrid,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  RadioButton,
  Checkbox,
  InlineLoading,
} from '@carbon/react';
import { EventData, EventRow, EventFilterOptions, TabType, SearchQueryParams } from '@/app/types';
import '@/app/styles/input-carbon.scss';
import './styles.scss';
import CarbonTabs from '@/components/TabSection/TabComponent';
import CarbonPagination from '@/components/Pagination/Pagination';
import { appendHostAndBasePath, validateBackslash } from '@/utils/urls';
import EventCard from '@/components/Cards/EventCards/EventCard';
import { ContentListResponseData, getContentListWithPagination } from '@/apis/EventAPIs';
import { formatDateTimeForTimezone } from '@/utils/formattingStrings';
import Link from 'next/link';
import { thisHostAndBasePath } from '../../../../config';
import { cleanMetadata } from '@/utils/cleanMetadata';
import CarbonAccordion from '@/components/Accordion/Accordion';
import SortDropdown, { SortOrder } from '@/components/SortDropdown/SortDropdown';
import '@/app/styles/loader-carbon.scss';
import '@/app/(pages)/content-page/common-popover.scss';
import AnalyticsTracker from '@/analytics/AnalyticsTracker';
import ContentEngagementClient from '@/analytics/ContentEngagementClient';

const PAGE_SIZE_OPTIONS: number[] = [12, 24, 32];

const TABLE_HEADERS = [
  { key: 'date', header: 'Date' },
  { key: 'event', header: 'Event' },
  { key: 'type', header: 'Type' },
  { key: 'location', header: 'Location' },
];

const EVENT_FORMATS = ['all', 'online', 'offline'] as const;
const EVENT_SUBTYPES = [
  'bootcamp',
  'conference',
  'hackathon',
  'meetup',
  // 'user group',
  'webinar',
] as const;

const EVENT_REGIONS = [
  'Asia Pacific',
  'Europe, Middle East, Africa',
  'Latin America (LATAM)',
  'North America',
] as const;

const REGION_API_MAP: Record<string, string> = {
  'Asia Pacific': 'ap',
  'Europe, Middle East, Africa': 'benelux',
  'Latin America (LATAM)': 'latam',
  'North America': 'na-west',
};

interface FiltersSidebarProps {
  filters: any;
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  technologyTags: string[];
}

function FiltersSidebar({ filters, setFilters, technologyTags }: FiltersSidebarProps) {
  const FilterItems = [
    {
      key: 'event-format',
      title: 'Event Format',
      openByDefault: true,
      content: EVENT_FORMATS.map(value => (
        <RadioButton
          key={value}
          labelText={value === 'all' ? 'All' : value === 'online' ? 'Digital' : 'In-person'}
          checked={filters?.event_medium === value}
          onChange={() => {
            setFilters((prev: any) => ({ ...prev, event_medium: value }));
          }}
        />
      )),
    },
    {
      key: 'event-subtype',
      title: 'Event Type',
      openByDefault: true,
      content: EVENT_SUBTYPES.map(value => (
        <Checkbox
          key={value}
          id={`event-type-${value}`}
          labelText={value.charAt(0).toUpperCase() + value.slice(1)}
          checked={filters?.subtype.includes(value)}
          onChange={() => {
            const nextSubtypes = filters?.subtype.includes(value)
              ? filters?.subtype.filter((x: string) => x !== value)
              : [...filters?.subtype, value];
            setFilters((prev: any) => ({ ...prev, subtype: nextSubtypes }));
          }}
        />
      )),
    },
    {
      key: 'region',
      title: 'Region',
      content: EVENT_REGIONS.map(value => (
        <Checkbox
          key={value}
          id={`event-region-${value}`}
          labelText={value}
          checked={filters?.region.includes(value)}
          onChange={() => {
            const nextRegion = filters?.region.includes(value)
              ? filters?.region.filter((x: string) => x !== value)
              : [...filters?.region, value];

            setFilters((prev: any) => ({ ...prev, region: nextRegion }));
          }}
        />
      )),
    },
    {
      key: 'tags',
      title: 'Technologies',
      content: technologyTags.map(value => (
        <Checkbox
          key={value}
          id={`event-tag-${value}`}
          labelText={value.charAt(0).toUpperCase() + value.slice(1)}
          checked={filters?.tags.includes(value)}
          onChange={() => {
            const nextTags = filters?.tags.includes(value)
              ? filters?.tags.filter((x: string) => x !== value)
              : [...filters?.tags, value];
            setFilters((prev: any) => ({ ...prev, tags: nextTags }));
          }}
        />
      )),
    },
  ];

  return (
    <div className="filters-sidebar">
      <CarbonAccordion items={FilterItems} />
    </div>
  );
}

function formatEventRows(events: EventData[]): EventRow[] {
  return events.map((event, index) => {
    const address = [
      event.event_address?.event_address_1,
      event.event_address?.event_city,
      event.event_address?.event_state_province,
      event.event_address?.event_country,
    ]
      .filter(Boolean)
      .join(', ');

    return {
      id: event.uuid ?? index.toString(),
      date: formatDateTimeForTimezone(event.event_start_date, event.timezone_abbr),
      event: (
        <Link href={validateBackslash(appendHostAndBasePath(`${thisHostAndBasePath}/events/${event.slug}`))}>
          {event.title || 'Untitled Event'}
        </Link>
      ),
      type: event.event_medium === 'online' ? 'Digital' : 'In person',
      location: event.event_medium === 'online' ? '' : address || 'TBD',
      originalData: event,
    };
  });
}

function getFiltersFromQuery(searchParams: URLSearchParams): EventFilterOptions {
  return {
    event_medium: (searchParams.get('fb') as any) ?? 'all',
    subtype: searchParams.get('subtype')?.split(',').filter(Boolean) ?? [],
    region: searchParams.get('region')?.split(',').filter(Boolean) ?? [],
    tags: searchParams.get('category')?.split(',').filter(Boolean) ?? [],
  };
}

interface EventListPageProps {
  eventPageParam: SearchQueryParams;
  events: EventData[];
  totalCount: number;
}

export default function EventListPage({
  eventPageParam,
  totalCount: parentTotalCount,
}: EventListPageProps) {
  const DEFAULT_SORT: SortOrder = 'oldest';
  const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
  const [upcomingEvents, setUpcomingEvents] = useState<EventRow[]>([]);
  const [pastEvents, setPastEvents] = useState<EventRow[]>([]);

  const [upcomingTotal, setUpcomingTotal] = useState(parentTotalCount ?? 0);
  const [pastTotal, setPastTotal] = useState(parentTotalCount ?? 0);

  const [upcomingPage, setUpcomingPage] = useState(1);
  const [upcomingPageSize, setUpcomingPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [pastPage, setPastPage] = useState(1);
  const [pastPageSize, setPastPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [loadingTab, setLoadingTab] = useState<'upcoming' | 'past' | null>(null);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const todayStr = new Date().toISOString().split('T')[0];

  const [sortBy, setSortBy] = useState<SortOrder>('oldest');

  const [technologyTags, setTechnologyTags] = useState<string[]>([]);
  const categoryCache = React.useRef<Partial<Record<TabType, string[]>>>({});

  const DEFAULT_FILTERS = {
    event_medium: 'all',
    subtype: [] as string[],
    region: [] as string[],
    tags: [] as string[],
  };

  const [filters, setFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      return getFiltersFromQuery(searchParams);
    }
    return DEFAULT_FILTERS;
  });

  async function loadCategories(newTab: TabType) {
    if (categoryCache.current[newTab]) {
      setTechnologyTags(categoryCache.current[newTab]);
      return;
    }

    const payload: any = { page: 1, size: 200 };
    if (newTab === 'past') payload.end_date = todayStr;

    const response: ContentListResponseData | null = await getContentListWithPagination(
      'events',
      payload,
    );

    if (!response || !response.results?.length) {
      setTechnologyTags([]);
      return;
    }

    const tags = Array.from(
      new Set((response?.results ?? []).flatMap(e => e.categories ?? []).filter(Boolean)),
    );

    categoryCache.current[newTab] = tags;
    setTechnologyTags(tags);
  }

  const loadEvents = useCallback(
    async (tab: 'upcoming' | 'past', page: number, pageSize: number) => {
      setLoadingTab(tab);
      const payload: Record<string, any> = {
        page,
        size: pageSize,
        sort: sortBy,
      };

      if (filters.event_medium !== 'all') payload.event_medium = filters.event_medium;
      if (filters.subtype.length) payload.subtype = filters.subtype;
      if (filters.region.length) payload.region = filters.region.map(r => REGION_API_MAP[r]);
      if (filters.tags.length) payload.tags = filters.tags;

      if (activeTab === 'past') payload.end_date = todayStr;

      try {
        const resList = await getContentListWithPagination('events', payload);
        const results: EventData[] = resList?.results ?? [];

        let totalItems: number;
        if (resList && 'header' in resList && resList.header?.totalResult != null) {
          totalItems = resList.header.totalResult;
        } else {
          totalItems = results.length;
        }

        const formatted = formatEventRows(results ?? []);
        if (tab === 'upcoming') {
          setUpcomingEvents(formatted);
          setUpcomingTotal(totalItems);
        } else {
          setPastEvents(formatted);
          setPastTotal(totalItems);
        }
      } catch (error) {
        console.error('Failed to load events', error);
      } finally {
        setLoadingTab(null);
      }
    },
    [filters, sortBy, activeTab],
  );

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    // Reset filters & sort & pagination
    setFilters(DEFAULT_FILTERS);
    setSortBy(DEFAULT_SORT);

    if (tab === 'upcoming') {
      setUpcomingPage(1);
      setUpcomingPageSize(DEFAULT_PAGE_SIZE);
    } else {
      setPastPage(1);
      setPastPageSize(DEFAULT_PAGE_SIZE);
    }

    // Load categories for new tab
    loadCategories(tab);
  };
  useEffect(() => {
    document.title = 'Events | IBM Developer';
  }, []);
  useEffect(() => {
    loadCategories(activeTab);
  }, [activeTab]);

  useEffect(() => {
    const page = activeTab === 'upcoming' ? upcomingPage : pastPage;
    const size = activeTab === 'upcoming' ? upcomingPageSize : pastPageSize;

    loadEvents(activeTab, page, size);
  }, [activeTab, upcomingPage, upcomingPageSize, pastPage, pastPageSize, filters, sortBy]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.event_medium !== 'all') params.set('fb', filters.event_medium);
    if (filters.subtype.length) params.set('subtype', filters.subtype.join(','));
    if (filters.region.length)
      params.set('region', filters?.region?.map(r => REGION_API_MAP[r])?.join(','));
    if (filters.tags.length) params.set('category', filters.tags.join(','));
    if (activeTab) params.set('fc', activeTab);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }, [filters]);

  const pageMetaData = cleanMetadata({
    content_type: 'events',
    slug: '/events/',
    publish_date: null,
    date_modified: null,
    url: '/events/',
    title: 'Event Page',
    favourite: '',
    like: '',
    follow: '',
    status: '',
    primary_category: '',
    tags: '', // keep as array
    strategic_content_area: '',
    authors: '', // array
    authors_email: '',
    editors: '',
    editors_email: '',
    record_type: 'contentType-page',
    secondary_tag: '',
  });

  return (
    <section className="events-page">
      <AnalyticsTracker />
      <ContentEngagementClient metaData={pageMetaData} />
      <FlexGrid className="container-medium event-container">
        <Row className="full-section" style={{ marginBottom: '1rem' }}>
          <Column sm={4} md={2} lg={2} className="side-nav">
            <button
              className="clearFilterBtn"
              onClick={() =>
                setFilters({
                  event_medium: 'all',
                  subtype: [],
                  region: [],
                  tags: [],
                })
              }
              disabled={
                filters.event_medium === 'all' &&
                filters.subtype.length === 0 &&
                filters.region.length === 0 &&
                filters.tags.length === 0
              }
            >
              Clear Filters
            </button>
            <FiltersSidebar
              filters={filters}
              setFilters={setFilters}
              technologyTags={technologyTags}
            />
          </Column>
          <Column lg={13} md={6}>
            <CarbonTabs
              // defaultKey={}
              onChange={key => {
                handleTabChange(key as TabType);
              }}
              items={[
                {
                  key: 'upcoming',
                  label: 'Upcoming Events',
                  content: (
                    <>
                      <div className="result-gp" style={{ paddingLeft: '1rem' }}>
                        {upcomingTotal !== 0 && (
                          <p className="results-info">
                            Showing {(upcomingPage - 1) * upcomingPageSize + 1} –{' '}
                            {Math.min(upcomingPage * upcomingPageSize, upcomingTotal)} of{' '}
                            {upcomingTotal} results
                          </p>
                        )}

                        <SortDropdown
                          sortOrder={sortBy}
                          onChange={nextSort => {
                            setSortBy(nextSort);
                          }}
                        />
                      </div>

                      {loadingTab === 'upcoming' ? (
                        <InlineLoading description="Loading..." />
                      ) : (
                        <CarbonPagination
                          // fetchData={(nextPage, size) => {}}
                          data={upcomingEvents}
                          totalItems={upcomingTotal}
                          loading={false}
                          defaultPageSize={DEFAULT_PAGE_SIZE}
                          currentPage={upcomingPage}
                          pageSize={upcomingPageSize}
                          pageSizeOptions={PAGE_SIZE_OPTIONS}
                          emptyMessage={
                            <span style={{ paddingLeft: '1rem' }}>
                              {'No upcoming events found.'}
                            </span>
                          }
                          listClassName="event-card-grid"
                          className="events-pagination"
                          onPageChange={page => {
                            setUpcomingPage(page);
                          }}
                          onPageSizeChange={size => {
                            setUpcomingPageSize(size);
                            setUpcomingPage(1); // reset to first page when pageSize changes
                          }}
                          renderItem={(item: EventRow | EventRow[], idx?: number) => {
                            const event = Array.isArray(item) ? item[0] : item;

                            return (
                              <Column
                                lg={5}
                                md={4}
                                sm={12}
                                key={event.id ?? idx}
                                className="event-inner-cards"
                              >
                                <EventCard
                                  key={event.id ?? idx}
                                  title={event.originalData.title ?? 'Untitled Event'}
                                  link={validateBackslash(appendHostAndBasePath(
                                    `/events/${event.originalData.slug ?? ''}`,
                                  ))}
                                  type={event.originalData.subtype ?? ''}
                                  location={
                                    event.originalData.event_address?.event_city || 'Digital'
                                  }
                                  date={event.date}
                                  timezone={event.originalData.timezone_abbr ?? ''}
                                />
                              </Column>
                            );
                          }}
                        ></CarbonPagination>
                      )}
                    </>
                  ),
                },

                {
                  key: 'past',
                  label: 'Past Events',
                  content: (
                    <>
                      <div className="result-gp" style={{ paddingLeft: '1rem' }}>
                        {pastTotal !== 0 && (
                          <p className="results-info">
                            Showing {(pastPage - 1) * pastPageSize + 1} –{' '}
                            {Math.min(pastPage * pastPageSize, pastTotal)} of {pastTotal} results
                          </p>
                        )}
                        <SortDropdown
                          sortOrder={sortBy}
                          onChange={nextSort => {
                            // setPage(1);
                            setSortBy(nextSort);
                          }}
                        />
                      </div>
                      {loadingTab === 'past' ? (
                        <InlineLoading description="Loading..." />
                      ) : (
                        <CarbonPagination
                          data={pastEvents}
                          totalItems={pastTotal}
                          loading={false}
                          defaultPageSize={DEFAULT_PAGE_SIZE}
                          currentPage={pastPage}
                          pageSize={pastPageSize}
                          pageSizeOptions={PAGE_SIZE_OPTIONS}
                          isEventTable
                          emptyMessage={
                            <span style={{ paddingLeft: '1rem' }}>{'No past events found.'}</span>
                          }
                          onPageChange={page => {
                            setPastPage(page);
                          }}
                          onPageSizeChange={size => {
                            setPastPageSize(size);
                            setPastPage(1); // reset to first page when pageSize changes
                          }}
                          renderItem={(rows: EventRow[] | EventRow) => (
                            <DataTable
                              rows={Array.isArray(rows) ? rows : [rows]}
                              headers={TABLE_HEADERS}
                            >
                              {({
                                rows,
                                headers,
                                getTableProps,
                                getHeaderProps,
                                getRowProps,
                                getCellProps,
                              }: any) => (
                                <Table {...getTableProps()}>
                                  <TableHead>
                                    <TableRow>
                                      {headers.map((header: any) => (
                                        <TableHeader
                                          {...getHeaderProps({ header: header })}
                                          key={header.key}
                                        >
                                          {header.header}
                                        </TableHeader>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {rows.map((row: any) => (
                                      <TableRow {...getRowProps({ row })} key={row.id}>
                                        {row.cells.map((cell: any) => (
                                          <TableCell {...getCellProps({ cell })} key={cell.id}>
                                            {cell.value}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </DataTable>
                          )}
                        />
                      )}
                    </>
                  ),
                },
              ]}
            ></CarbonTabs>
          </Column>
        </Row>
      </FlexGrid>
    </section>
  );
}
